<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <div class="header d-flex align-items-center justify-content-between">
            <h2>Document Types</h2>
            <b-button variant="primary btn-mw" size="lg" @click="openCreateModal">
              Add Document Type <i class="fa fa-plus-square ml-1"></i>
            </b-button>
          </div>
          <div class="body pt-0">
            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-md-4">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search by name or code..."
                  v-model="searchQuery"
                />
              </div>
              <div class="col-md-3">
                <select class="form-control" v-model="filterCategory">
                  <option value="">All Categories</option>
                  <option value="business">Business</option>
                  <option value="identity">Identity</option>
                  <option value="address">Address</option>
                  <option value="financial">Financial</option>
                  <option value="regulatory">Regulatory</option>
                  <option value="edd">EDD</option>
                </select>
              </div>
              <div class="col-md-3">
                <select class="form-control" v-model="filterActive">
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <!-- Table -->
            <b-table
              responsive
              hover
              outlined
              table-class="js-basic-example table-custom mb-0"
              head-variant="light"
              :items="filteredDocumentTypes"
              :fields="fields"
              :per-page="perPage"
              :current-page="currentPage"
              :busy="isLoading"
              show-empty
              :emptyText="'No document types found.'"
              striped
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <b-spinner variant="primary"></b-spinner>
                  <p class="mt-2">Loading...</p>
                </div>
              </template>

              <template #cell(name)="{ item }">
                <div>
                  <strong>{{ item.name }}</strong>
                  <br />
                  <small class="text-muted">{{ item.code }}</small>
                </div>
              </template>

              <template #cell(category)="{ item }">
                <span class="badge" :class="categoryBadgeClass(item.category)">
                  {{ item.category }}
                </span>
              </template>

              <template #cell(has_expiry)="{ item }">
                <i v-if="item.has_expiry" class="fa fa-check text-success"></i>
                <i v-else class="fa fa-times text-muted"></i>
              </template>

              <template #cell(allowed_mime_types)="{ item }">
                <span
                  v-for="(mime, i) in item.allowed_mime_types"
                  :key="i"
                  class="badge badge-light mr-1"
                >
                  {{ mimeLabel(mime) }}
                </span>
              </template>

              <template #cell(max_file_size_mb)="{ item }">
                {{ item.max_file_size_mb }} MB
              </template>

              <template #cell(is_active)="{ item }">
                <span
                  class="badge"
                  :class="item.is_active ? 'badge-success' : 'badge-danger'"
                >
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </span>
              </template>

              <template #cell(actions)="{ item }">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  @click="openEditModal(item)"
                  title="Edit"
                >
                  <i class="fa fa-pencil"></i>
                </b-button>
              </template>
            </b-table>

            <div
              class="py-2 d-flex align-items-center justify-content-end"
              v-if="filteredDocumentTypes.length > perPage"
            >
              <b-pagination
                class="pagination-small"
                v-model="currentPage"
                :total-rows="filteredDocumentTypes.length"
                :per-page="perPage"
                first-text="First"
                last-text="Last"
                align="right"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <b-modal
      id="doctype-modal"
      :title="editingItem ? 'Edit Document Type' : 'Add Document Type'"
      hide-footer
      size="lg"
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <div class="modal-area">
        <ValidationObserver v-slot="{ passes }">
          <form @submit.prevent="passes(saveDocType)">
            <div class="row">
              <!-- Code -->
              <div class="col-md-6">
                <ValidationProvider name="code" rules="required" v-slot="{ errors }">
                  <div class="form-group" :class="{ errors: errors[0], success: form.code && !errors[0] }">
                    <label>Code *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="form.code"
                      placeholder="e.g. tax_clearance"
                      :disabled="!!editingItem"
                    />
                    <small v-if="!editingItem" class="text-muted">Unique identifier. Cannot be changed later.</small>
                    <span class="text-danger error-msg" v-if="errors[0]">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Name -->
              <div class="col-md-6">
                <ValidationProvider name="name" rules="required" v-slot="{ errors }">
                  <div class="form-group" :class="{ errors: errors[0], success: form.name && !errors[0] }">
                    <label>Name *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="form.name"
                      placeholder="e.g. Tax Clearance Certificate"
                    />
                    <span class="text-danger error-msg" v-if="errors[0]">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Description -->
              <div class="col-md-12">
                <div class="form-group">
                  <label>Description</label>
                  <textarea
                    class="form-control"
                    v-model="form.description"
                    rows="2"
                    placeholder="Brief description of this document type"
                  ></textarea>
                </div>
              </div>

              <!-- Category -->
              <div class="col-md-6">
                <ValidationProvider name="category" rules="required" v-slot="{ errors }">
                  <div class="form-group" :class="{ errors: errors[0], success: form.category && !errors[0] }">
                    <label>Category *</label>
                    <select class="form-control" v-model="form.category">
                      <option value="">Select category</option>
                      <option value="business">Business</option>
                      <option value="identity">Identity</option>
                      <option value="address">Address</option>
                      <option value="financial">Financial</option>
                      <option value="regulatory">Regulatory</option>
                      <option value="edd">EDD</option>
                    </select>
                    <span class="text-danger error-msg" v-if="errors[0]">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Max File Size -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Max File Size (MB)</label>
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="form.max_file_size_mb"
                    min="1"
                    max="50"
                  />
                </div>
              </div>

              <!-- Allowed MIME Types -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Accepted File Types *</label>
                  <div class="d-flex flex-wrap">
                    <b-form-checkbox
                      v-model="form.mime_pdf"
                      class="mr-3"
                    >PDF</b-form-checkbox>
                    <b-form-checkbox
                      v-model="form.mime_jpeg"
                      class="mr-3"
                    >JPEG</b-form-checkbox>
                    <b-form-checkbox
                      v-model="form.mime_png"
                      class="mr-3"
                    >PNG</b-form-checkbox>
                  </div>
                </div>
              </div>

              <!-- Flags -->
              <div class="col-md-6">
                <div class="form-group">
                  <label class="d-block">Options</label>
                  <div class="d-flex flex-wrap">
                    <b-form-checkbox v-model="form.has_expiry" class="mr-3">Has Expiry Date</b-form-checkbox>
                    <b-form-checkbox v-model="form.is_active">Active</b-form-checkbox>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end mt-3">
              <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('doctype-modal')">
                Cancel
              </b-button>
              <b-button variant="primary" type="submit" :disabled="saving">
                <b-spinner small v-if="saving" class="mr-1"></b-spinner>
                {{ editingItem ? 'Update' : 'Create' }}
              </b-button>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </b-modal>

    <main-loader ref="loader"></main-loader>
  </div>
</template>

<script>
import shared from "@/shared.js";
import { mapState } from "vuex";
import "@/plugins/vee-validate";

const { ValidationObserver, ValidationProvider } = require("vee-validate");
const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;

export default {
  name: "DocumentTypesConfig",
  components: {
    ValidationObserver,
    ValidationProvider,
    "main-loader": LoaderComponent,
  },
  data() {
    return {
      searchQuery: "",
      filterCategory: "",
      filterActive: "",
      currentPage: 1,
      perPage: 20,
      fields: [
        { key: "name", label: "Document Type", sortable: true },
        { key: "category", label: "Category", sortable: true },
        { key: "has_expiry", label: "Expiry" },
        { key: "allowed_mime_types", label: "File Types" },
        { key: "max_file_size_mb", label: "Max Size" },
        { key: "is_active", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
      editingItem: null,
      saving: false,
      form: this.getEmptyForm(),
    };
  },
  computed: {
    ...mapState("config", ["documentTypes", "isLoading"]),
    filteredDocumentTypes() {
      let items = this.documentTypes;
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        items = items.filter(
          (d) =>
            d.name.toLowerCase().includes(q) ||
            d.code.toLowerCase().includes(q)
        );
      }
      if (this.filterCategory) {
        items = items.filter((d) => d.category === this.filterCategory);
      }
      if (this.filterActive) {
        const active = this.filterActive === "true";
        items = items.filter((d) => d.is_active === active);
      }
      return items;
    },
  },
  mounted() {
    this.loadDocTypes();
  },
  methods: {
    getEmptyForm() {
      return {
        code: "",
        name: "",
        description: "",
        category: "",
        max_file_size_mb: 10,
        has_expiry: false,
        is_active: true,
        mime_pdf: true,
        mime_jpeg: true,
        mime_png: true,
      };
    },

    async loadDocTypes() {
      this.$refs.loader.show();
      try {
        await this.$store.dispatch("config/fetchDocumentTypes");
      } catch (e) {
        shared.toastrError("Failed to load document types");
      } finally {
        this.$refs.loader.hide();
      }
    },

    openCreateModal() {
      this.editingItem = null;
      this.form = this.getEmptyForm();
      this.$bvModal.show("doctype-modal");
    },

    openEditModal(item) {
      this.editingItem = item;
      const mimes = item.allowed_mime_types || [];
      this.form = {
        code: item.code,
        name: item.name,
        description: item.description || "",
        category: item.category,
        max_file_size_mb: item.max_file_size_mb || 10,
        has_expiry: item.has_expiry,
        is_active: item.is_active,
        mime_pdf: mimes.includes("application/pdf"),
        mime_jpeg: mimes.includes("image/jpeg"),
        mime_png: mimes.includes("image/png"),
      };
      this.$bvModal.show("doctype-modal");
    },

    buildMimeTypes() {
      const mimes = [];
      if (this.form.mime_pdf) mimes.push("application/pdf");
      if (this.form.mime_jpeg) mimes.push("image/jpeg");
      if (this.form.mime_png) mimes.push("image/png");
      return mimes;
    },

    async saveDocType() {
      const mimes = this.buildMimeTypes();
      if (mimes.length === 0) {
        shared.toastrError("Please select at least one accepted file type");
        return;
      }

      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description || null,
          category: this.form.category,
          max_file_size_mb: this.form.max_file_size_mb,
          has_expiry: this.form.has_expiry,
          is_active: this.form.is_active,
          allowed_mime_types: mimes,
        };

        if (this.editingItem) {
          await this.$store.dispatch("config/updateDocumentType", {
            id: this.editingItem.id,
            payload,
          });
          shared.toastrSuccess("Document type updated successfully");
        } else {
          payload.code = this.form.code;
          await this.$store.dispatch("config/createDocumentType", payload);
          shared.toastrSuccess("Document type created successfully");
        }

        this.$bvModal.hide("doctype-modal");
        this.loadDocTypes();
      } catch (e) {
        const msg = e.response?.data?.message || "Failed to save document type";
        shared.toastrError(msg);
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      this.editingItem = null;
      this.form = this.getEmptyForm();
    },

    mimeLabel(mime) {
      const map = {
        "application/pdf": "PDF",
        "image/jpeg": "JPEG",
        "image/png": "PNG",
      };
      return map[mime] || mime;
    },

    categoryBadgeClass(category) {
      const map = {
        business: "badge-primary",
        identity: "badge-success",
        address: "badge-info",
        financial: "badge-warning",
        regulatory: "badge-danger",
        edd: "badge-dark",
      };
      return map[category] || "badge-secondary";
    },
  },
};
</script>
